import { useState } from 'react';
import { Check, ChevronDown, Copy, Link2, Maximize2, MoreHorizontal, Trash2 } from 'lucide-react';

import type { AvailableTransition, Issue } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { Dropdown } from '../../components/Dropdown';
import { IconButton } from '../../components/IconButton';
import { Tooltip } from '../../components/Tooltip';
import styles from './IssuePanelHeader.module.css';

export type IssuePanelHeaderProps = {
  readonly issue: Issue;
  readonly transitions: readonly AvailableTransition[];
  readonly onTransition: (transition: AvailableTransition) => void;
  readonly onDuplicate: () => void;
  readonly onCopyLink: () => void;
  readonly onDelete: () => void;
  /** Открыть задачу на её странице; на самой странице не показывается. */
  readonly onOpenPage?: () => void;
  readonly viewers: readonly { readonly id: string; readonly login: string; readonly displayName: string; readonly avatarColor?: string }[];
};

/** Шапка панели: ключ, тип, кнопка «Переход», меню «…», присутствие. */
export function IssuePanelHeader({
  issue,
  transitions,
  onTransition,
  onDuplicate,
  onCopyLink,
  onDelete,
  onOpenPage,
  viewers,
}: IssuePanelHeaderProps) {
  const [isTransitionMenuOpen, setTransitionMenuOpen] = useState(false);

  return (
    <div className={styles['header']}>
      <div className={styles['top']}>
        <span className={styles['key']}>{issue.key}</span>
        <span className={styles['type']}>{issue.type}</span>

        {viewers.length > 0 ? (
          <Tooltip content={`Смотрят сейчас: ${viewers.map((viewer) => viewer.displayName).join(', ')}`}>
            <span className={styles['viewers']} aria-label="Присутствие">
              {viewers.slice(0, 3).map((viewer) => (
                <span
                  key={viewer.id}
                  className={styles['viewerDot']}
                  style={{ background: viewer.avatarColor ?? 'var(--c-done)' }}
                  title={viewer.displayName}
                />
              ))}
            </span>
          </Tooltip>
        ) : null}

        <span className={styles['spacer']} />

        <div className={styles['transitionWrap']}>
          <Button
            size="sm"
            variant="secondary"
            icon={<ChevronDown size={14} />}
            disabled={transitions.length === 0}
            aria-haspopup="menu"
            aria-expanded={isTransitionMenuOpen}
            onClick={() => setTransitionMenuOpen((open) => !open)}
          >
            {issue.status.name}
          </Button>
          {isTransitionMenuOpen && transitions.length > 0 ? (
            <ul className={styles['transitionMenu']} role="menu">
              {transitions.map((transition) => (
                <li key={transition.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setTransitionMenuOpen(false);
                      onTransition(transition);
                    }}
                  >
                    <Check aria-hidden size={14} />
                    {transition.name}
                    <small>{transition.toStatus.name}</small>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <Dropdown
          align="end"
          trigger={({ toggle }) => (
            <IconButton label="Действия с задачей" icon={<MoreHorizontal size={18} />} onClick={toggle} />
          )}
          items={[
            {
              id: 'duplicate',
              label: 'Дублировать',
              icon: <Copy size={14} />,
              onSelect: onDuplicate,
            },
            { id: 'link', label: 'Копировать ссылку', icon: <Link2 size={14} />, onSelect: onCopyLink },
            { id: 'delete', label: 'Удалить', icon: <Trash2 size={14} />, danger: true, onSelect: onDelete },
          ]}
        />

        {/* Рядом с крестиком панели: до страницы задачи — один клик. */}
        {onOpenPage ? (
          <IconButton
            label="Открыть на весь экран"
            tooltip="Открыть на весь экран"
            icon={<Maximize2 size={18} />}
            onClick={onOpenPage}
          />
        ) : null}
      </div>
    </div>
  );
}
